// src/pages/Clients/List.js

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  Chip,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Badge,
  Tooltip,
  Stack,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  ViewList as ListViewIcon,
  ViewModule as GridViewIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Search as SearchIcon,
  ViewColumn as ColumnsIcon,
  PeopleAlt as PeopleIcon,
  PersonAddAlt as NewClientIcon,
  Star as StarIcon,
  EmojiEvents as VipIcon
} from '@mui/icons-material';
import { DataGrid, GridOverlay } from '@mui/x-data-grid';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import clientsApi from '../../api/clientsApi';
import { useAppTheme } from '../../contexts/ThemeContext';
import ClientCard from '../../components/clients/ClientCard';

import { alpha, useTheme } from '@mui/material/styles';

const CustomNoRowsOverlay = () => (
  <GridOverlay>
    <Box p={2}>
      <Typography variant="subtitle1">No clients found</Typography>
    </Box>
  </GridOverlay>
);

const ClientsList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentTheme } = useAppTheme();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [totalClients, setTotalClients] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'full_name', sort: 'asc' }]);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [statusFilter, setStatusFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({
    full_name: true,
    email: true,
    mobile: true,
    is_active: true,
    created_date: true
  });
  const [editColumnsOpen, setEditColumnsOpen] = useState(false);
  const controllerRef = useRef();
  const fetchTriggerRef = useRef(0);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    vip: 0,
    newThisMonth: 0
  });

  const statHighlights = [
    {
      label: 'Total Clients',
      helper: 'All patient records in your workspace',
      value: stats.total,
      accent: 'primary',
      icon: PeopleIcon
    },
    {
      label: 'Active Clients',
      helper: 'Currently engaged and reachable',
      value: stats.active,
      accent: 'success',
      icon: StarIcon
    },
    {
      label: 'VIP Clients',
      helper: 'High-touch or priority accounts',
      value: stats.vip,
      accent: 'warning',
      icon: VipIcon
    },
    {
      label: 'New This Month',
      helper: 'Recently added prospects',
      value: stats.newThisMonth,
      accent: 'secondary',
      icon: NewClientIcon
    }
  ];

  const debouncedSearch = useCallback(
    debounce(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500),
    []
  );

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    debouncedSearch();
  };

  const mapSortFieldToBackend = (field) => {
    switch (field) {
      case 'full_name':
        return 'full_name';
      case 'email':
        return 'email';
      case 'created_date':
        return 'created_date';
      case 'mobile':
        return 'created_date';
      case 'is_active':
        return 'created_date';
      default:
        return 'created_date';
    }
  };

  const fetchClients = async () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);

    try {
      const order_by = sortModel.length > 0 ? mapSortFieldToBackend(sortModel[0].field) : 'created_date';
      const order_direction = sortModel.length > 0 && sortModel[0].sort ? sortModel[0].sort : 'desc';

      console.log(`Fetching clients: page=${paginationModel.page}, pageSize=${paginationModel.pageSize}, order_by=${order_by}`);

      const response = await clientsApi.getClients({
        skip: paginationModel.page * paginationModel.pageSize,
        limit: paginationModel.pageSize,
        search: search || undefined,
        status: statusFilter || undefined,
        label: labelFilter || undefined,
        order_by,
        order_direction,
        tier_filter: false
      });

      console.log('API Response:', response);

      if (response && Array.isArray(response.clients)) {
        const clientsWithIds = response.clients.map((client) => {
          if (!client.id && client._id) {
            return { ...client, id: client._id };
          }
          return client;
        });

        console.log('Processed clients:', clientsWithIds);
        setClients(clientsWithIds);
        setTotalClients(response.total || clientsWithIds.length);

        setStats({
          total: response.total || 0,
          active: response.total || clientsWithIds.filter((c) => c.is_active).length,
          vip: 0,
          newThisMonth: clientsWithIds.filter((c) => {
            const created = new Date(c.created_date);
            const now = new Date();
            return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
          }).length
        });
      } else {
        console.error('Invalid response format:', response);
        setClients([]);
        setTotalClients(0);
        setStats({ total: 0, active: 0, vip: 0, newThisMonth: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);

      if (error.response?.status === 422) {
        console.error('API Parameter validation error:', error.response.data);
      } else if (error.response?.status === 401) {
        console.error('Authentication error - redirecting to login');
      } else if (error.response?.status >= 500) {
        console.error('Server error:', error.response.status);
      } else {
        console.error('Network or other error:', error.message);
      }

      setClients([]);
      setTotalClients(0);
      setStats({ total: 0, active: 0, vip: 0, newThisMonth: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTriggerRef.current += 1;
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, sortModel, search, statusFilter, labelFilter]);

  const handleRowClick = (params) => {
    navigate(`/clients/${params.row.id || params.row._id}`);
  };

  const handleColumnVisibilityChange = (field) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleEditColumnsClose = () => {
    setEditColumnsOpen(false);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleFilterMenuOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
  };

  const handleExportMenuOpen = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setLabelFilter('');
    setSearch('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (statusFilter) count += 1;
    if (labelFilter) count += 1;
    if (search) count += 1;
    return count;
  };

  const handleClientClick = (client) => {
    navigate(`/clients/${client.id || client._id}`);
  };

  const handleClientMenuClick = (event, client) => {
    console.log('Client menu clicked:', client);
  };

  const allColumns = [
    {
      field: 'full_name',
      headerName: 'Client Name',
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight="bold">
            {params.row.full_name || `${params.row.first_name || ''} ${params.row.last_name || ''}`.trim()}
          </Typography>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.email || 'No email'}
        </Typography>
      )
    },
    {
      field: 'mobile',
      headerName: 'Phone',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.mobile || params.row.phone || 'No phone'}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? 'Active' : 'Inactive'}
          color={params.row.is_active ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'created_date',
      headerName: 'Created Date',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.created_date
            ? new Date(params.row.created_date).toLocaleDateString()
            : 'No date'}
        </Typography>
      )
    }
  ];

  const visibleColumns = allColumns.filter((column) => columnVisibility[column.field]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: 'background.default' }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 3 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={0.5}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Clients
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Live sync keeps this list current - {totalClients.toLocaleString()} clients on file
            </Typography>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          {statHighlights.map((stat) => {
            const Icon = stat.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={stat.label}>
                <Card
                  elevation={0}
                  sx={(theme) => {
                    const accent = theme.palette?.[stat.accent]?.main || theme.palette.primary.main;
                    return {
                      borderRadius: 3,
                      height: '100%',
                      border: '1px solid',
                      borderColor: alpha(accent, 0.18),
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: '0 18px 38px rgba(15, 23, 42, 0.08)'
                    };
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box
                          sx={(theme) => {
                            const accent = theme.palette?.[stat.accent]?.main || theme.palette.primary.main;
                            return {
                              width: 44,
                              height: 44,
                              borderRadius: 2,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(accent, 0.12),
                              color: accent
                            };
                          }}
                        >
                          <Icon fontSize="medium" />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', maxWidth: 160 }}>
                          {stat.helper}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                          {stat.label}
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary
                          }}
                        >
                          {stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            boxShadow: '0 20px 48px rgba(15, 23, 42, 0.1)'
          }}
        >
          <Stack
            direction={{ xs: 'column', xl: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', xl: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexGrow: 1 }}>
              <TextField
                placeholder="Search clients..."
                variant="outlined"
                size="small"
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 999
                  }
                }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Badge badgeContent={getActiveFiltersCount()} color="primary" overlap="circular">
                  <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={handleFilterMenuOpen}
                    sx={{ borderRadius: 999, px: 2.5 }}
                  >
                    Filter
                  </Button>
                </Badge>
                {viewMode === 'list' && (
                  <Button
                    variant="outlined"
                    startIcon={<ColumnsIcon />}
                    onClick={() => setEditColumnsOpen(true)}
                    sx={{ borderRadius: 999, px: 2.5 }}
                  >
                    Columns
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={handleExportMenuOpen}
                  sx={{ borderRadius: 999, px: 2.5 }}
                >
                  Export
                </Button>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                View
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                sx={(theme) => ({
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  borderRadius: 999,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.5
                  },
                  '& .Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    boxShadow: '0 14px 30px rgba(99, 102, 241, 0.35)'
                  }
                })}
              >
                <ToggleButton value="list">
                  <Tooltip title="List View">
                    <ListViewIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="grid">
                  <Tooltip title="Grid View">
                    <GridViewIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          {getActiveFiltersCount() > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Active filters:
              </Typography>
              {statusFilter && (
                <Chip
                  label={`Status: ${statusFilter}`}
                  size="small"
                  onDelete={() => setStatusFilter('')}
                  sx={{ borderRadius: 999 }}
                />
              )}
              {labelFilter && (
                <Chip
                  label={`Label: ${labelFilter}`}
                  size="small"
                  onDelete={() => setLabelFilter('')}
                  sx={{ borderRadius: 999 }}
                />
              )}
              <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>
                Clear All
              </Button>
            </Stack>
          )}
        </Paper>

        {viewMode === 'list' ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.85),
              overflow: 'hidden',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)'
            }}
          >
            <Box sx={{ height: 620 }}>
              <DataGrid
                rows={clients}
                columns={visibleColumns}
                getRowId={(row) => row.id || row._id || Math.random().toString(36).substring(2, 11)}
                loading={loading}
                pagination
                paginationMode="server"
                rowCount={totalClients}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                  console.log('Pagination model changed:', model);
                  setPaginationModel(model);
                }}
                pageSizeOptions={[25, 50, 100]}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={(newSortModel) => {
                  console.log('Sort model changed:', newSortModel);
                  setSortModel(newSortModel);
                }}
                checkboxSelection
                disableSelectionOnClick
                components={{
                  NoRowsOverlay: CustomNoRowsOverlay
                }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd'
                }
                sx={(theme) => ({
                  border: 0,
                  cursor: 'pointer',
                  fontSize: '0.92rem',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: 0.3
                  },
                  '& .MuiDataGrid-row': {
                    transition: 'background-color 160ms ease, transform 160ms ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12)
                    }
                  },
                  '& .MuiDataGrid-row.row-even': {
                    backgroundColor: theme.palette.background.paper
                  },
                  '& .MuiDataGrid-row.row-odd': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.035)
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiDataGrid-cellContent': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: '100%'
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    height: 64,
                    px: 2
                  },
                  '& .MuiDataGrid-selectedRowCount': {
                    visibility: 'hidden'
                  }
                })}
                onRowClick={handleRowClick}
              />
            </Box>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              p: { xs: 2, md: 3 },
              boxShadow: '0 24px 56px rgba(15, 23, 42, 0.1)'
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <Typography>Loading clients...</Typography>
              </Box>
            ) : clients.length === 0 ? (
              <Box textAlign="center" p={4}>
                <Typography variant="h6" color="text.secondary">
                  No clients found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search or filters
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={3}>
                  {clients.map((client) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={client.id || client._id}>
                      <ClientCard
                        client={client}
                        onClick={handleClientClick}
                        onMenuClick={handleClientMenuClick}
                        showScore
                      />
                    </Grid>
                  ))}
                </Grid>

                <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    Showing {paginationModel.page * paginationModel.pageSize + 1}-{Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalClients)} of {totalClients} clients
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      disabled={paginationModel.page === 0}
                      onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2">
                      Page {paginationModel.page + 1} of {Math.ceil(totalClients / paginationModel.pageSize)}
                    </Typography>
                    <Button
                      disabled={paginationModel.page >= Math.ceil(totalClients / paginationModel.pageSize) - 1}
                      onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Next
                    </Button>
                  </Stack>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Stack>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterMenuClose}
      >
        <Box sx={{ p: 2, minWidth: 260 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filter Clients
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel size="small">Status</InputLabel>
            <Select
              size="small"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel size="small">Label</InputLabel>
            <Select
              size="small"
              value={labelFilter}
              label="Label"
              onChange={(e) => setLabelFilter(e.target.value)}
            >
              <MenuItem value="">All Labels</MenuItem>
              <MenuItem value="VIP">VIP</MenuItem>
              <MenuItem value="Regular">Regular</MenuItem>
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Loyal">Loyal</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={clearFilters}>
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleFilterMenuClose}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Menu>

      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportMenuClose}
      >
        <MenuItem onClick={() => { console.log('Export CSV'); handleExportMenuClose(); }}>
          Export as CSV
        </MenuItem>
        <MenuItem onClick={() => { console.log('Export Excel'); handleExportMenuClose(); }}>
          Export as Excel
        </MenuItem>
        <MenuItem onClick={() => { console.log('Export PDF'); handleExportMenuClose(); }}>
          Export as PDF
        </MenuItem>
      </Menu>

      <Dialog open={editColumnsOpen} onClose={handleEditColumnsClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Columns</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which columns to display in the table:
          </Typography>
          <List>
            {allColumns.map((column) => (
              <ListItem key={column.field} sx={{ px: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={columnVisibility[column.field]}
                      onChange={() => handleColumnVisibilityChange(column.field)}
                    />
                  }
                  label={column.headerName}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditColumnsClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsList;
